import type {
  BrowserCommand,
  BrowserSessionSnapshot,
  DesktopNativeBrowserResult,
  EnvironmentApi,
  EnvironmentId,
  ThreadId,
} from "@cmmd-forge/contracts";
import type { BrowserControlSequencer } from "@cmmd-forge/client-runtime";
import { withBrowserOperationTimeout } from "./BrowserPreviewPanel.logic";
import type { BrowserCommandRpcResult } from "./BrowserPreviewPanel.commandResult";

export type NativeBrowserCommand = NonNullable<
  NonNullable<Window["desktopBridge"]>["nativeBrowser"]
>["command"];

type BrowserCommandExecutionPlanVm = ReturnType<
  typeof import("./BrowserPreviewPanel.logic").resolveBrowserCommandExecutionPlan
>;

export function browserControlTargetForSession(
  threadId: ThreadId,
  session: BrowserSessionSnapshot,
) {
  return session.sessionEpoch
    ? {
        threadId,
        browserId: session.browserId,
        sessionEpoch: session.sessionEpoch,
        lastAppliedControlSequence: session.lastAppliedControlSequence ?? 0,
      }
    : null;
}

interface RunNativeBrowserCommandInput {
  readonly command: BrowserCommand;
  readonly nativeCommand: NativeBrowserCommand;
  readonly session: BrowserSessionSnapshot;
  readonly tabId: string;
  readonly timeoutMs: number | undefined;
  readonly onNativeResult: (result: DesktopNativeBrowserResult) => void;
}

async function runNativeBrowserCommand(input: RunNativeBrowserCommandInput): Promise<boolean> {
  const result = await withBrowserOperationTimeout(
    `Native browser ${input.command.type}`,
    input.nativeCommand({
      browserId: input.session.browserId,
      sessionId: input.session.sessionId,
      tabId: input.tabId,
      command: input.command,
    }),
    input.timeoutMs,
  );
  input.onNativeResult(result);
  return result.attached;
}

interface RunServerBrowserCommandInput {
  readonly browserApi: NonNullable<EnvironmentApi["browser"]>;
  readonly command: BrowserCommand;
  readonly controlSequencer: BrowserControlSequencer;
  readonly environmentId: EnvironmentId;
  readonly session: BrowserSessionSnapshot | null;
  readonly threadId: ThreadId;
  readonly timeoutMs: number;
  readonly onResult: (result: BrowserCommandRpcResult) => boolean;
}

async function runServerBrowserCommand(input: RunServerBrowserCommandInput): Promise<boolean> {
  const execute = (identity?: {
    readonly sessionEpoch: string;
    readonly controlSequence: number;
  }) => {
    const commandInput = {
      threadId: input.threadId,
      environmentId: input.environmentId,
      ...(input.session?.browserId ? { browserId: input.session.browserId } : {}),
      command: input.command,
    };
    return input.browserApi.command(identity ? { ...commandInput, ...identity } : commandInput);
  };
  const target = input.session
    ? browserControlTargetForSession(input.threadId, input.session)
    : null;
  const request: Promise<BrowserCommandRpcResult> = target
    ? (() => {
        input.controlSequencer.register(target);
        return input.controlSequencer.enqueue<BrowserCommandRpcResult>({
          threadId: target.threadId,
          browserId: target.browserId,
          execute,
        });
      })()
    : execute();
  const result = await withBrowserOperationTimeout(
    `Browser ${input.command.type}`,
    request,
    input.timeoutMs,
  );
  return input.onResult(result);
}

export interface DispatchBrowserCommandInput {
  readonly browserApi: EnvironmentApi["browser"];
  readonly command: BrowserCommand;
  readonly commandPlan: BrowserCommandExecutionPlanVm;
  readonly controlSequencer: BrowserControlSequencer;
  readonly environmentId: EnvironmentId;
  readonly nativeCommand: NativeBrowserCommand | undefined;
  readonly onNativeResult: (result: DesktopNativeBrowserResult) => void;
  readonly onServerResult: (result: BrowserCommandRpcResult) => boolean;
  readonly session: BrowserSessionSnapshot | null;
  readonly threadId: ThreadId;
  readonly timeoutMs: number | undefined;
}

export async function dispatchBrowserCommand(input: DispatchBrowserCommandInput): Promise<boolean> {
  const nativeTabId = input.commandPlan.commandRoute.nativeCommandTabId;
  if (
    input.commandPlan.commandRoute.shouldUseNativeBrowserCommand &&
    input.nativeCommand &&
    nativeTabId &&
    input.session
  ) {
    return await runNativeBrowserCommand({
      command: input.command,
      nativeCommand: input.nativeCommand,
      session: input.session,
      tabId: nativeTabId,
      timeoutMs: input.timeoutMs,
      onNativeResult: input.onNativeResult,
    });
  }

  return await runServerBrowserCommand({
    browserApi: input.browserApi!,
    command: input.command,
    controlSequencer: input.controlSequencer,
    environmentId: input.environmentId,
    session: input.session,
    threadId: input.threadId,
    timeoutMs: input.commandPlan.serverCommandTimeoutMs,
    onResult: input.onServerResult,
  });
}
